import { CrmSyncService } from '@shared/services/crm-sync.service';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ParametryService } from '@pages/parametry/parametry.service';
import { Consorcista, ConsorcistasService, OccupancyRole } from '@pages/consorcios/consorcistas.service';
import { Complain, ComplainResolver, newComplain, StatusComplain } from './complains.config';
import { agentByDefault, Amenity, CocheraNivel, CocheraSinNivel, Consorcio, getCocheraLabel, Service, UrgentService } from './consorcios.config';
import { ConsorciosService } from './consorcios.service';
import { ComplainsService } from './complains.service';
import { QuestionsService } from './questions.service';
import { TransfersService } from './transfers.service';
import { newQuestion, Question, StatusQuestion } from './questions.config';
import { newTransfer, StatusTransfer, Transfer } from './transfers.config';
import { UploadService } from '@shared/services/upload.service';
import { Photo } from '@pages/orders/service.store';
import { Channel } from './interaction.abstract';
import { CocheraConsorcista, CocheraConsorcistaPending, CocherasService } from './cocheras.service';
import { ExpensesService } from './expenses.service';

@Injectable({
  providedIn: 'root'
})
export class ConsorciosManagerService {
  /* Este servicio es para aplicar la logica de negocio y hacer las operaciones complejas. aqui se pueden llamar todos los servicios que sean necesarios. */
  parametryService = inject(ParametryService);
  consorciosService = inject(ConsorciosService);
  consorcistasService = inject(ConsorcistasService);
  complainsService = inject(ComplainsService);
  questionsService = inject(QuestionsService);
  transfersService = inject(TransfersService);
  expensesService = inject(ExpensesService);
  uploadService = inject(UploadService);
  cocherasService = inject(CocherasService);
  crmSyncService = inject(CrmSyncService);

  constructor() { }

  async rejectConsorcista(id:string,consorcioId:string){
    const consorcista = await this.consorcistasService.updatePending(consorcioId,id, {status:'rejected'});
    return consorcista;
  }
  async acceptConsorcista(id:string,consorcio:Consorcio,data: Partial<Consorcista>){
    //cambia el estado del consorcista pendiente a aceptado y luego lo crea en la coleccion de consorcistas
    await this.consorcistasService.updatePending(consorcio.uid,id, {status:'accepted'});
    //dado que se crea a partir de un consorcista pendiente, se obtiene ese consorcista para crear el nuevo consorcista con los mismos datos pero se quita el status
    if(data['status']){
      delete data['status'];
    }
    //la consulta/reclamo que haya dejado al registrarse recien se convierte en Question/Complain al aceptar, no se guarda en el consorcista real
    const pendingInteraction = data['pendingInteraction'];
    if(data['pendingInteraction']){
      delete data['pendingInteraction'];
    }
    const consorcistaId = await this.consorcistasService.create(consorcio.uid, data);
    //crear/actualizar el contacto en el CRM del agente (sync-contacto-crm lo crea si no existe). Sin esperar: el servicio nunca tira error
    this.crmSyncService.syncConsorcista(consorcio.uid, consorcistaId.toString());
    if(data['cocheras']){
      //completar datos de cocheras
      data['cocheras'] = data['cocheras'].map((c:CocheraConsorcista) => {
        return {
          ...c,
          consorcioId: consorcio.uid,
          consorcistaId: consorcistaId.toString(),
          createdAt: new Date(),
          occupancyRole: c.occupancyRole || OccupancyRole.Owner,
          firstName: data['firstName'] || '',
          lastName: data['lastName'] || '',
          countryCode: data['countryCode'] || '549',
          phoneArea: data['phoneArea'] || '',
          phoneNumber: data['phoneNumber'] || '',
        };
      });
      //crear cocheras
      data['cocheras'].forEach( async cochera => {
        await this.assignGarageToConsorcista(cochera, consorcio.cocheras! ,consorcio.garageLevels??true, consorcio.uid, consorcistaId.toString())   
      });
    }

    if(pendingInteraction?.text){
      const consorcistaSnapshot = {
        uid: consorcistaId.toString(),
        firstName: data['firstName'] || '',
        lastName: data['lastName'] || '',
        floor: data['floor'] || '',
        apartment: data['apartment'] || '',
      };
      //canal 'Whatsapp' porque el alta siempre viene de un link de autoregistro enviado por WhatsApp (antes decía
      //'handmade' y se mostraba "Manual" en la lista, que era incorrecto). El status NO se deriva del canal como en
      //otras creaciones (ver createComplain/createQuestion más abajo): se fuerza a 'actionRequired' explícitamente
      //después de crear el registro, porque acá nadie —ni bot ni humano— intentó resolverlo todavía.
      const wspId = (data['phoneNumber'] && data['phoneArea'])
        ? `${data['countryCode'] || '549'}${data['phoneArea']}${data['phoneNumber']}@s.whatsapp.net`
        : undefined;
      if(pendingInteraction.type === 'complain'){
        const complain = await this.createComplain({
          resume: pendingInteraction.text.slice(0, 80),
          detail: pendingInteraction.text,
          urgent: false,
          canal: 'Whatsapp',
          wspId: wspId,
          administrationUserId: consorcio.administrationUserId,
          closedBy: 'agent',
        }, { consorcio, consorcista: consorcistaSnapshot, forceStatus: 'actionRequired' });
      }else{
        const question = await this.createQuestion({
          resume: pendingInteraction.text.slice(0, 80),
          type: 'any',
          canal: 'Whatsapp',
           wspId: wspId,
          administrationUserId: consorcio.administrationUserId,
          closedBy: 'agent',
        }, { consorcio, consorcista: consorcistaSnapshot,forceStatus: 'actionRequired' });
      }
    }
    return consorcistaId;
  }

  async resolveComplain(id:string,consorcioId:string){
    //cierra el reclamo, no olvidarse de crear una orden antes
    const complain = await this.complainsService.update(consorcioId,id, {status:'working'});
    return complain;
  }
  async closedComplain(id:string,consorcioId:string,resolutionNote?:string,resolvedBy?:string,resolvers?:ComplainResolver[]){
    //cierra el reclamo, no olvidarse de crear una orden antes
    const data: any = {status:'closed',closedAt: new Date()};
    if(resolutionNote){
      data.resolutionNote = resolutionNote;
      if(resolvedBy){
        data.resolvedBy = resolvedBy;
      }
    }
    if(resolvers && resolvers.length > 0){
      data.resolvers = resolvers;
    }
    const complain = await this.complainsService.update(consorcioId,id, data);
    return complain;
  }
  async rejectComplain(id:string,consorcioId:string){
    const complain = await this.complainsService.update(consorcioId,id, {status:'deny',closedAt: new Date()});
    return complain;
  }
  async answerQuestion(id:string,consorcioId:string, channel:Channel){
    //se llama luego de responder
    //el estado vuelve a open porque ahora queda a la espera de que el cliente responda. El agente retoma el mando
    //como hubo intervención humana, se considerada cerrada por un humano
    if(channel === 'handmade'){
      return;
    }
    const question = await this.questionsService.update(consorcioId,id, {status:'open', closedBy:'human'});
    return question;
  }
  async resolveQuestion(id:string,consorcioId:string){
    //para marcar como resuelto de forma manual
    //como hubo intervención humana, se considerada cerrada por un humano
    const question = await this.questionsService.update(consorcioId,id, {status:'closed', closedBy:'human',closedAt: new Date()});
    return question;
  }
  async rejectQuestion(id:string,consorcioId:string){
    const question = await this.questionsService.update(consorcioId,id, {status:'ignored', closedBy:'human',closedAt: new Date()});
    return question;
  }
  async resolveExpense(id:string,consorcioId:string){
    //para marcar como resuelto de forma manual
    //como hubo intervención humana, se considerada cerrada por un humano
    const expense = await this.expensesService.update(consorcioId,id, {status:'processed', closedBy:'human',closedAt: new Date()});
    return expense;
  }
  async rejectExpense(id:string,consorcioId:string){
    const expense = await this.expensesService.update(consorcioId,id, {status:'deny', closedBy:'human',closedAt: new Date()});
    return expense;
  }
  async resolveTransfer(transfer:Transfer, consorcio:Consorcio){
    //solo reemplaza los consorcistas de la misma unidad que tengan el mismo rol de ocupacion

    //borrar consorcistas de esa unidad
    let consorcistas = await firstValueFrom(this.consorcistasService.searchByUnitAndName(transfer.consorcioId, transfer.floor, transfer.apartment, "",""));
    if(transfer.tower){
      consorcistas = consorcistas.filter(c => c.tower === transfer.tower);
    }
    //quitar los demas roles
    consorcistas = consorcistas.filter(c => c.occupancyRole === transfer.consorcista.occupancyRole);

    //borrar cocheras de estos consorcistas a borrar
    consorcistas.forEach( async cons => {
      //traer sus cocheras, si tiene
      let deleteCocheras= await firstValueFrom(this.cocherasService.search({consorcioId:consorcio.uid, consorcistaId:cons.uid}));
      deleteCocheras.forEach(async cochera => {
        //borrar cochera y actualizar ocupacion
        await this.deleteGarageConsorcista(cochera, consorcio.cocheras! ,consorcio.garageLevels??true, consorcio.uid, cochera.consorcistaId.toString());
      });
    });
    
    //borrar consorcistas
    await this.consorcistasService.deleteMany(transfer.consorcioId, consorcistas);

    //agregar whatsapp id 

    transfer.consorcistasNew.map(c=>{
      c.consorcioId = transfer.consorcioId;
     if(c.phoneNumber && c.phoneArea){
        c.countryCode="549";
        c.wspId= c.countryCode + c.phoneArea + c.phoneNumber + '@s.whatsapp.net';
      }
      c.floor = transfer.floor;
      c.apartment = transfer.apartment;
      if(transfer.tower){
        c.tower = transfer.tower;
      }
      return c;
    })

     //para obtener el id del primer consorcista creado
      let mainConsorcistaId: string | undefined;
    //agregar nuevos consorcistas a esa unidad
     try {
      for (const [index, consorcista] of transfer.consorcistasNew.entries()) {
      let consorcistaId = await this.consorcistasService.create(transfer.consorcioId.toString(), consorcista)
      //crear/actualizar el contacto en el CRM del agente (sync-contacto-crm lo crea si no existe). Sin esperar: el servicio nunca tira error
      this.crmSyncService.syncConsorcista(transfer.consorcioId.toString(), consorcistaId.toString());
      if(index === 0){
        mainConsorcistaId = consorcistaId.toString();
      }
      if(consorcista.cocheras){
        //completar datos de cocheras
        consorcista['cocheras'] = consorcista['cocheras'].map((c:CocheraConsorcista) => {
          return {
            ...c,
            consorcioId: consorcio.uid,
            consorcistaId: consorcistaId.toString(),
            createdAt: new Date(),
            occupancyRole: c.occupancyRole || OccupancyRole.Owner,
            firstName: consorcista['firstName'] || '',
            lastName: consorcista['lastName'] || '',
            countryCode: consorcista['countryCode'] || '549',
            phoneArea: consorcista['phoneArea'] || '',
            phoneNumber: consorcista['phoneNumber'] || '',
          };
        });
        
        consorcista.cocheras.forEach( async cochera => {
          //conseguir los cochera consorcista de esa unidad de cochera
          let deleteCocheras= await firstValueFrom(this.cocherasService.search({consorcioId:consorcio.uid, nivel: cochera.nivel?.toString(), numero: cochera.numero}));
          //filtrar por rol de ocupacion de cochera
          deleteCocheras=deleteCocheras.filter(dc=>cochera.occupancyRole==dc.occupancyRole);
          deleteCocheras.forEach(async cochera => {
            //borrar cochera y actualizar ocupacion
            await this.deleteGarageConsorcista(cochera, consorcio.cocheras! ,consorcio.garageLevels??true, consorcio.uid, cochera.consorcistaId.toString());
            //borrar consorcistas de esa cochera
            //Revisar si el consorcista es de solo garage
            const consorcistacoch = await firstValueFrom(this.consorcistasService.getConsorcistaById(consorcio.uid, cochera.consorcistaId.toString()));
            if(consorcistacoch && consorcistacoch.onlyGarage){
              let cocherasConsorcista = await this.cocherasService.searchCount({consorcioId:consorcio.uid, consorcistaId: consorcistacoch.uid});
              if(cocherasConsorcista === 0){
                  await this.deleteConsorcista(consorcistacoch, consorcio);
              }
            }
          });
          //crear cocheras
          await this.assignGarageToConsorcista(cochera, consorcio.cocheras! ,consorcio.garageLevels??true, consorcio.uid, consorcistaId.toString())   
        });
      }
      
     }
     } catch (error) {
      throw new Error(`error al crear las cocheras`); 
      
     }
      
      //la consulta/reclamo que haya dejado al registrar la mudanza recién se convierte en Question/Complain al aceptarla,
    //no se guarda en la transferencia. Mismo criterio que en acceptConsorcista() más arriba (canal 'Whatsapp' +
    //status forzado a 'actionRequired'), duplicado acá porque son dos flujos de alta distintos (directa vs mudanza).
    if(transfer.pendingInteraction?.text && mainConsorcistaId){
      const consorcistaSnapshot = {
        uid: mainConsorcistaId,
        firstName: transfer.consorcista?.firstName || '',
        lastName: transfer.consorcista?.lastName || '',
        floor: transfer.floor || '',
        apartment: transfer.apartment || '',
      };
      const wspId = transfer.consorcistasNew[0]?.wspId;
      if(transfer.pendingInteraction.type === 'complain'){
        const complain = await this.createComplain({
          resume: transfer.pendingInteraction.text.slice(0, 80),
          detail: transfer.pendingInteraction.text,
          urgent: false,
          canal: 'Whatsapp',
          wspId: wspId,
          administrationUserId: consorcio.administrationUserId,
          closedBy: 'agent',
        }, { consorcio, consorcista: consorcistaSnapshot });
        await this.complainsService.update(consorcio.uid || '', complain.uid, { status: 'actionRequired' });
      }else{
        const question = await this.createQuestion({
          resume: transfer.pendingInteraction.text,
          type: 'any',
          canal: 'Whatsapp',
          wspId: wspId,
          administrationUserId: consorcio.administrationUserId,
          closedBy: 'agent',
        }, { consorcio, consorcista: consorcistaSnapshot });
        await this.questionsService.update(consorcio.uid || '', question.uid, { status: 'actionRequired' });
      }
    }
    
    
    //actualizar el estado de la mudanza
    const transferUpdated = await this.transfersService.update(transfer.consorcioId, transfer.uid, {status:'accepted',closedAt: new Date()});
    return transferUpdated;
  }
  async rejectTransfer(transfer:Transfer){
    const transferUpdated = await this.transfersService.update(transfer.consorcioId, transfer.uid, {status:'deny',closedAt: new Date()});
    return transferUpdated;
  }


  /* TODO revisar la creacion de la tarea en add pliego y add orden para traer todo aqui excepto la creacion de la orden */
  /* crear la orden implica obtener el nuevo taskNumber (y updatearlo?) y crear la bill asociada */
  async createConsorcio(consorcioData: Omit<Consorcio,
     'uid' | 'nro' | 'createdAt' | 'id'>
     ,options:{
    amenities?:Amenity[],
    services?:Service[],
    urgentServices?:UrgentService[]
  }): Promise<Consorcio> {

    consorcioData['createdAt'] = new Date();
    consorcioData['nro'] = await this.consorciosService.numberTransaction();
    consorcioData['agentPersistence']=1
    
     const consorcioId = await this.consorciosService.create(consorcioData);
    consorcioData['uid'] = consorcioId.toString();

    //crear las subcolecciones
    await this.consorciosService.createManyAmenity(consorcioId.toString(), options.amenities || []);
    await this.consorciosService.createManyServices(consorcioId.toString(), options.services || []);
    await this.consorciosService.createManyUrgentServices(consorcioId.toString(), options.urgentServices || []);
    await this.consorciosService.createmanyAgentQuestions(consorcioId.toString(), agentByDefault);

   
    return consorcioData as Consorcio;
  }

  async createComplain(complainData: newComplain,  options: {
    consorcio:Consorcio,
    consorcista: {uid:string,firstName:string, lastName:string, floor:string, apartment:string},
    images?:Photo[],
     forceStatus?: StatusComplain,
  }): Promise<Complain> {
    complainData['createdAt'] = new Date();
    complainData['status'] = options.forceStatus ?? (complainData.canal=='handmade'?'actionRequired':'open');
    complainData['consorcioId'] = options.consorcio.uid || '';
    complainData['consorcioName'] = `${options.consorcio.name || 'Sin nombre'} - ${options.consorcio.location?.address || ''} ${options.consorcio.location?.streetNumber || ''}`;
    complainData['consorcista'] = options.consorcista;
    complainData['consorcistaId'] = options.consorcista.uid;

    let nroComplain =await this.complainsService.countComplains(options.consorcio.uid || '') ;
    nroComplain +=  1;
    let countComplains:string =(options.consorcio.nro|| options.consorcio.uid) +"-RCL"  +  nroComplain;
    complainData['nro'] = countComplains;

    //imagenes
    if(options.images && options.images.length > 0){
        const urlPhotos: { name: string; url: string }[] = [];
        const uploadPromises = options.images?.map(
        async (photo, index) => {
          const rawBase64 = photo.imageBase64.split(',').pop()!;
          const result = await this.uploadService.uploadFileFromBase64(
            rawBase64,
            'consorcio/'+options.consorcio.uid+'/complains/' + countComplains +'/' + index + '.jpg'
          );
          const photoToSave: Photo = {
            imageBase64: '',
            name: index + '.jpg',
            type: 'image/jpeg',
            url: result,
          };
          urlPhotos.push(photoToSave);
        });
        await Promise.all(uploadPromises)
        complainData['images'] = urlPhotos;
    }

    //crear el reclamo
     const complainId = await this.complainsService.create(options.consorcio.uid || '', complainData);
     complainData['uid'] = complainId.toString();
      return complainData as Complain;
  }
  async createQuestion(questionData: newQuestion,  options: {
    consorcio:Consorcio,
    consorcista: {uid:string,firstName:string, lastName:string, floor:string, apartment:string},
    forceStatus?: StatusQuestion,
  }): Promise<Question> {
    questionData['createdAt'] = new Date();
    questionData['status'] = options.forceStatus ?? (questionData.canal=='handmade'?'actionRequired':'open');
    questionData['consorcioId'] = options.consorcio.uid || '';
    questionData['consorcioName'] = `${options.consorcio.name || 'Sin nombre'} - ${options.consorcio.location?.address || ''} ${options.consorcio.location?.streetNumber || ''}`;
    questionData['consorcista'] = options.consorcista;
    questionData['consorcistaId'] = options.consorcista.uid;
     let nroQuestions =await this.questionsService.countQuestions(options.consorcio.uid || '') ;
    nroQuestions +=  1;
    questionData['nro'] = (options.consorcio.nro|| options.consorcio.uid) +"-Q"  +  nroQuestions;
    questionData['consorcioName'] = `${options.consorcio.name || 'Sin nombre'} - ${options.consorcio.location?.address || ''} ${options.consorcio.location?.streetNumber || ''}`;
    const questionId = await this.questionsService.create(options.consorcio.uid || '', questionData);
    questionData['uid'] = questionId.toString();
    return questionData as Question;
  }
  async createTransfer(transferData: newTransfer,  options: {
    consorcio:Consorcio,
    consorcista: {uid?:string,firstName:string, lastName:string, floor:string, apartment:string},
    forceStatus?: StatusTransfer,
  }): Promise<Transfer> { 
    transferData['createdAt'] = new Date();
    transferData['status'] = options.forceStatus ?? (transferData.canal=='handmade'?'actionRequired':'open');
    transferData['consorcioId'] = options.consorcio.uid || '';
    transferData['consorcioName'] = options.consorcio.name || '';
    transferData['consorcista'] = options.consorcista;
    transferData['consorcistaId'] = options.consorcista.uid || '';
    let nroTransfers =await this.transfersService.countTransfers(options.consorcio.uid || '') ;
    nroTransfers +=  1;
    transferData['nro'] = (options.consorcio.nro|| options.consorcio.uid) +"-T"  +  nroTransfers;
    const transferId = await this.transfersService.create(options.consorcio.uid || '', transferData);
    transferData['uid'] = transferId.toString();
    return transferData as Transfer;
  }

  async assignGarageToConsorcista(cochera:CocheraConsorcista,cocherasData:CocheraNivel[] | CocheraSinNivel,garageLevels:boolean, consorcioId:string, consorcistaId:string){
    //crear cochera (la asignacion de cochera se hace creando una cochera con el consorcista asignado)
    cochera.numero= Number(+cochera.numero)
    cochera.consorcistaId=consorcistaId;
    //guardar tambien la etiqueta de la cochera (si tiene) para mostrarla sin buscar en el consorcio
    const label = getCocheraLabel(cocherasData, cochera.nivel, cochera.numero);
    if (label) cochera.label = label;    
    
    const cocheraCreated = await this.cocherasService.create(consorcioId,consorcistaId,cochera);
    
    //actualizar la ocupacion de la cochera en el consorcio
    try{
      if(garageLevels){
        let nivelIndex = (cocherasData as CocheraNivel[]).findIndex(c => c.nivel === cochera.nivel);
        let unidadIndex = (cocherasData as CocheraNivel[])[nivelIndex].unidades.findIndex(u =>u.numero === cochera.numero);
        //no Borrar Consolelog, si no no funcion crear el  consorcista 
        const unidad = (cocherasData as CocheraNivel[])[nivelIndex].unidades[unidadIndex];
        if (!unidad) {
          throw new Error(`Unidad en índice ${unidadIndex} es undefined`);
        }
        
        (cocherasData as CocheraNivel[])[nivelIndex].unidades[unidadIndex].ocupada = true;
      }else{
        let unidadIndex = (cocherasData as CocheraSinNivel).unidades.findIndex(u =>u.numero === cochera.numero);
        const unidad = (cocherasData as CocheraSinNivel).unidades[unidadIndex];
        
        if (!unidad) {
          throw new Error(`Unidad en índice ${unidadIndex} es undefined`);
        }
        
        (cocherasData as CocheraSinNivel).unidades[unidadIndex].ocupada = true;
      }
      await this.consorciosService.update(consorcioId, {cocheras: cocherasData});
    }catch(error){
      console.error("Error al actualizar la ocupacion de la cochera en el consorcio",error);
    }
    return cocheraCreated;
  }
  async deleteGarageConsorcista(cochera:CocheraConsorcista,cocherasData:CocheraNivel[]|CocheraSinNivel,garageLevels:boolean, consorcioId:string, consorcistaId:string){
    //borrar cochera
    const cocheraDeleted = await this.cocherasService.delete(consorcioId,consorcistaId,cochera.uid!);
    //actualizar la ocupacion de la cochera en el consorcio
    try{
      if(garageLevels){
        //verificar que no haya otros consorcistas asignados a esa cochera antes de marcarla como desocupada
        const consorcistasAsignados = await this.cocherasService.searchCount({consorcioId, nivel: cochera.nivel, numero: cochera.numero});
        if(consorcistasAsignados > 0){
          return cocheraDeleted;
        }

        let nivelIndex = (cocherasData as CocheraNivel[]).findIndex(c => c.nivel === cochera.nivel);
        let unidadIndex = (cocherasData as CocheraNivel[])[nivelIndex].unidades.findIndex(u =>u.numero === cochera.numero);
        const unidad = (cocherasData as CocheraNivel[])[nivelIndex].unidades[unidadIndex];

        if (!unidad) {
          throw new Error(`Unidad en índice ${unidadIndex} es undefined`);
        }
        
        (cocherasData as CocheraNivel[])[nivelIndex].unidades[unidadIndex].ocupada = false;
    }else{
        //verificar que no haya otros consorcistas asignados a esa cochera antes de marcarla como desocupada
        const consorcistasAsignados = await this.cocherasService.searchCount({consorcioId, numero: cochera.numero});
        if(consorcistasAsignados > 0){
          return cocheraDeleted;
        }

        let unidadIndex = (cocherasData as CocheraSinNivel).unidades.findIndex(u =>u.numero === cochera.numero);
        const unidad = (cocherasData as CocheraSinNivel).unidades[unidadIndex];

        if (!unidad) {
          throw new Error(`Unidad en índice ${unidadIndex} es undefined`);
        }

        (cocherasData as CocheraSinNivel).unidades[unidadIndex].ocupada = false;
    }
      await this.consorciosService.update(consorcioId, {cocheras: cocherasData});
    }catch(error){
      console.error("Error al actualizar la ocupacion de la cochera en el consorcio",error);
    }
    return cocheraDeleted;
  }

  async deleteConsorcista(consorcista:Consorcista, consorcio:Consorcio){
    const consorcioId = consorcista.consorcioId!;
    const cocherasData = await firstValueFrom(this.cocherasService.getByConsorcistaId(consorcioId, consorcista.uid!));
    //borrar consorcista
    const consorcistaDeleted = await this.consorcistasService.delete(consorcioId, consorcista.uid!);
    //y su contacto en el CRM del agente (sin esperar: el servicio nunca tira error). Pasa por acá también
    //el borrado automático de los consorcistas de solo cochera
    this.crmSyncService.deleteConsorcista(consorcioId, consorcista.uid!);
    if(cocherasData && cocherasData.length > 0) {
      try{
        for (const cochera of cocherasData) {
          this.deleteGarageConsorcista(cochera, consorcio.cocheras!, consorcio.garageLevels??true, consorcioId, consorcista.uid!);
        }
      }catch(error){
        console.error("Error al actualizar la ocupacion de la cochera en el consorcio",error);
      }
    }
    return consorcistaDeleted;
  }

  async acceptPendingCochera(id:string,consorcioId:string,consorcistaId:string, cochera: CocheraConsorcista, garageLevels:boolean, cocherasData:CocheraNivel[]|CocheraSinNivel){
    if(cochera['status']){
      delete cochera['status'];
    }
    try{
      await this.assignGarageToConsorcista(cochera,cocherasData,garageLevels, consorcioId, consorcistaId);
      await this.cocherasService.updatePending(consorcioId, id, {status:'accepted'});
    }catch(error){
      console.error("Error al actualizar la ocupacion de la cochera en el consorcio",error);
      //se relanza para que la pantalla no saque la fila ni avise "aceptada" si en realidad falló
      throw error;
    }
  }

  async rejectCochera(id:string,consorcioId:string){
    const cochera = await this.cocherasService.updatePending(consorcioId,id, {status:'rejected'});
    return cochera;
  }

}